export const ENTRY_STATUS = {
  DRAFT: 0,
  OPEN: 1,
  REVERIFY: 2,
  VERIFIED: 3,
  AUDIT: 4,
  FURTHER_INFO_REQUESTED: 5,
  FURTHER_INFO_PROVIDED: 6,
  PENDING: 8,
  PENDING_AUDIT: 50,
  WITHDRAWN: 99,
  INTERNAL_AUDIT_VERIFIED: 200,
  SUPERINTENDENT_VISIT_VERIFIED: 210,
  MANAGER_VISIT_VERIFIED: 220,
  PERIODIC_INSPECTION_VERIFIED: 230,
  MANAGEMENT_REVIEW: 240,
  YACHT_REVIEW: 250,
  AUDIT_COMPLETE: 1000,
} as const;

export interface IEntryStatusDef {
  label: string;
  /** Badge colour; undefined falls back to "blue" in StatusBadge. */
  color?: string;
  miw?: number;
  /** Summary page wording/styling ("Review" instead of "Audit" etc.). */
  summary: { label: string; color: string; fontWeight: string };
}

const bold = (label: string, color: string) => ({
  label,
  color,
  fontWeight: "bold",
});
const plain = (label: string) => ({ label, color: "", fontWeight: "" });

export const entryStatusMap: Record<number, IEntryStatusDef> = {
  0: { label: "Draft", color: "red.9", summary: bold("Draft", "red.9") },
  1: { label: "Open", color: "orange.4", summary: bold("Open", "orange.4") },
  2: {
    label: "Re-Verify",
    color: "orange.4",
    summary: bold("Re-Verify", "orange.4"),
  },
  3: {
    label: "Yacht Verified",
    color: "green.8",
    summary: bold("Yacht Verified", "green.8"),
  },
  4: { label: "Audit", summary: plain("Review") },
  5: {
    label: "Further Info Requested",
    miw: 200,
    summary: plain("Further Info Requested"),
  },
  6: {
    label: "Further Info Provided",
    miw: 200,
    summary: plain("Further Info Provided"),
  },
  8: {
    label: "Pending",
    color: "yellow.6",
    summary: bold("Pending", "yellow.6"),
  },
  50: { label: "Pending Audit", miw: 120, summary: plain("Pending Review") },
  99: { label: "Withdrawn", miw: 200, summary: bold("Withdrawn", "gray.7") },
  200: {
    label: "Internal Audit Verified",
    miw: 150,
    summary: plain("Internal Review Verified"),
  },
  210: {
    label: "Superintendent Visit Verified",
    miw: 200,
    summary: plain("Superintendent Visit Verified"),
  },
  220: {
    label: "Manager Visit Verified",
    miw: 150,
    summary: plain("Manager Visit Verified"),
  },
  230: {
    label: "Periodic Inspection Verified",
    miw: 200,
    summary: plain("Periodic Inspection Verified"),
  },
  240: {
    label: "Management Review",
    miw: 120,
    summary: plain("Management Review"),
  },
  250: { label: "Yacht Review", miw: 120, summary: plain("Yacht Review") },
  1000: { label: "Audit", summary: bold("Audit", "green.8") },
};

export const getEntryStatusLabel = (
  status: number | string | null | undefined,
) => {
  const def = entryStatusMap[Number(status)];
  return def ? def.label : String(status ?? "");
};

/** Draft, Open, Re-Verify or Pending — entry not yet verified. */
export const isUnverifiedStatus = (status: number | string) =>
  Number(status) < ENTRY_STATUS.VERIFIED ||
  Number(status) === ENTRY_STATUS.PENDING;

/** Draft, Open or Pending. */
export const isOpenLikeStatus = (status: number | string) =>
  Number(status) < ENTRY_STATUS.REVERIFY ||
  Number(status) === ENTRY_STATUS.PENDING;

/** Verified or any later review/audit status (excludes Pending). */
export const isVerifiedOrLaterStatus = (status: number | string) =>
  Number(status) > ENTRY_STATUS.REVERIFY &&
  Number(status) !== ENTRY_STATUS.PENDING;
